/**
 * Client-side poll loop for async SkyWrench jobs (Option A).
 *
 * While the modal is open this polls a job to completion, updates local state,
 * and persists the terminal result onto the event record so it survives close.
 * The backend poller (Option B) does the same server-side, so this loop is
 * purely for live in-modal updates: whichever observes completion first
 * persists it; the other becomes a no-op.
 */
import { SkyWrenchResult } from '../../types';
import {
  pollSkyWrenchJobOnce,
  persistSkyWrenchResult,
  buildSkyWrenchResult,
  buildSkyWrenchError,
  IN_PROGRESS_STATUSES,
  SkyWrenchQueryInput,
} from '../../services/skyWrenchService';

/** Poll cadence and ceiling. Analysis typically completes in a few minutes. */
const POLL_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS = 600_000; // 10 minutes

/** Promise-based delay between polls. */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Reconstructs the query-context input from a (pending) result so terminal
 * results rebuilt during polling carry the same display fields.
 */
export function inputFromResult(result: SkyWrenchResult): SkyWrenchQueryInput {
  return {
    aircraftType: result.aircraftType,
    ataCode: result.ataCode,
    defectDescription: result.defectDescription,
    tailNumber: result.tailNumber,
    carrier: result.carrier,
    station: result.station,
  };
}

/**
 * Callbacks the loop uses to report a terminal result and to check whether it
 * should keep running (false once the modal closes / component unmounts).
 */
export interface PollHandlers {
  onTerminal: (result: SkyWrenchResult) => void;
  isActive: () => boolean;
}

/**
 * Polls a single job until it reaches a terminal state, the modal closes, or
 * the ceiling is hit. On completion/failure it reports the result and persists
 * it to the event record. Transient poll errors are tolerated until the
 * deadline; the backend poller remains a safety net if this gives up.
 */
export async function pollJobToCompletion(
  jobId: string,
  defectId: number,
  input: SkyWrenchQueryInput,
  eventId: string,
): Promise<SkyWrenchResult | null> {
  const deadline = Date.now() + POLL_TIMEOUT_MS;

  try {
    while (Date.now() < deadline) {
      await delay(POLL_INTERVAL_MS);

      let job;
      try {
        job = await pollSkyWrenchJobOnce(jobId);
      } catch {
        continue; // transient; retry until the deadline
      }

      const status = (job.status || '').toUpperCase();
      if (status === 'COMPLETED') {
        const result = buildSkyWrenchResult(defectId, input, job.result, jobId);
        await persistSkyWrenchResult(eventId, result);
        return result;
      }
      if (status === 'FAILED' || !IN_PROGRESS_STATUSES.has(status)) {
        const message = job.error || `SkyWrench returned status: ${job.status || 'unknown'}`;
        const result = buildSkyWrenchError(defectId, input, message, jobId);
        await persistSkyWrenchResult(eventId, result);
        return result;
      }
    }
  } catch {
    // Unexpected loop failure; the backend poller remains the safety net.
    return null;
  }

  return null; // timed out client-side; backend poller will still finish it
}

export { POLL_TIMEOUT_MS };
